import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from '@angular/router';
import { environment } from '../../environments/environment';

/**
 * SocialMediaDomainGuard
 *
 * Enforces the white-label social media referral domain.
 *
 * When the app is served on one of the configured `socialMediaHosts`
 * (environment.socialMediaHosts), ONLY the social media referral flow is
 * reachable:
 *    - /social-media-register/:code   (agency-branded signup entry)
 *    - /register                      (customer application after signup)
 *    - /social-media-thank-you        (terminal thank-you)
 *
 * Any attempt to access/route to any other page (login, signup, admin,
 * dashboards, etc.) on the social media domain is redirected back to the
 * agency-branded signup page the visitor came from. Senem RFP branding/pages
 * are never exposed on the white-label domain.
 *
 * On the normal Senem RFP domain(s) this guard is a no-op (allows everything).
 */
@Injectable({
  providedIn: 'root',
})
export class SocialMediaDomainGuard {
  // Route path prefixes that are allowed on the social media domain.
  private readonly allowedPrefixes = [
    '/social-media-register',
    '/register',
    '/social-media-thank-you',
  ];

  // localStorage key used to remember the agency link code so we can send the
  // visitor back to THEIR branded signup page on any blocked navigation.
  private readonly codeStorageKey = 'socialMediaFirmCode';

  constructor(private router: Router) {}

  /** True when the app is currently served on a configured social media host. */
  isSocialMediaDomain(): boolean {
    const hosts = (environment as any).socialMediaHosts as string[] | undefined;
    if (!hosts || hosts.length === 0) return false;
    const currentHost = (window.location.hostname || '').toLowerCase();
    return hosts.some((h) => (h || '').toLowerCase() === currentHost);
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    // Not the white-label domain -> behave like the normal app.
    if (!this.isSocialMediaDomain()) {
      return true;
    }

    const url = (state.url || '').split('?')[0];

    // Remember the agency link code from the entry URL so we can redirect back
    // to the correct agency-branded page later.
    const codeFromUrl = next.paramMap.get('code');
    if (codeFromUrl) {
      try {
        localStorage.setItem(this.codeStorageKey, codeFromUrl);
      } catch {
        /* ignore storage errors */
      }
    }

    // Allow only the social media referral flow routes.
    const isAllowed = this.allowedPrefixes.some(
      (p) => url === p || url.startsWith(p + '/')
    );
    if (isAllowed) {
      return true;
    }

    // Blocked route on the social media domain -> redirect back to the agency
    // signup page (using the remembered code when available).
    let code = '';
    try {
      code = localStorage.getItem(this.codeStorageKey) || '';
    } catch {
      code = '';
    }

    if (code) {
      return this.router.createUrlTree(['/social-media-register', code]);
    }

    // No code known (e.g. visitor typed the bare white-label domain like
    // https://uat.insrfp.com/). Send them to the code-less social-media entry
    // route, which renders the "invalid link" state. This NEVER falls back to a
    // Senem RFP home/login page.
    return this.router.createUrlTree(['/social-media-register']);
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    return this.canActivate(next, state);
  }
}
